import { CPLAT_API_ROUTES, sellerQueries, UploadSystem, usePost } from '@/api';
import PencilIcon from '@/assets/icons/More/eva_edit-fill.svg?react';
import ProfileIcon from '@/assets/icons/More/IconUserprofile.svg?react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/molecules/Avatar';
import useCheckMembership from '@/hooks/useCheckMembership';
import { useLog } from '@/hooks/V3/useLog';
import { useToast } from '@/hooks/V3/useToast';
import { useAppDispatch, useAppSelector } from '@/redux/store';
import { setUser } from '@/redux/userSlice';
import { ROUTES } from '@/routes';
import type { CplatApiResponse, UserData } from '@/types/api';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { LayoutTabSection } from '../orders/_layouts/LayoutTabSection';
import GetOutClient from './GetOutClient';
import { useQuery } from '@tanstack/react-query';

export type FormData = {
  newStoreName: string; // 원하는 스토어 이름 아직 중복검사 x
  checkedNewStoreName: string; // 중복검사를 통과한 새로운 스토어 이름
  profileImgUrl: string;
  profileFile?: File;
};

const TabSection = () => {
  const user = useAppSelector((state) => state.user);
  const { setUserEventHistory } = useLog(user.UserNo);
  const { StoreName, StoreThumbnailImageUrl } = user;

  const getUserInfoQuery = useQuery(sellerQueries.getInfo({ cplatToken: user.CplatToken }));

  const userInfoData = getUserInfoQuery.data?.data?.[0];

  const { register, watch, setError, formState, setValue } = useForm<FormData>({
    defaultValues: {
      checkedNewStoreName: '',
      newStoreName: StoreName,
      profileImgUrl: userInfoData?.sellerImageUrl,
    },
  });

  const [isOpen, setIsOpen] = useState(false);
  const [isImgUploading, setIsImgUploading] = useState(false);

  const { showToast } = useToast();

  const formValues = watch();

  const { newStoreName, checkedNewStoreName, profileImgUrl, profileFile } = formValues;
  const { errors } = formState;

  const { mutate: checkDuplicateStoreName } = usePost(CPLAT_API_ROUTES.hasStoreName);
  const { mutate: setUserInfo, isPending } = usePost(CPLAT_API_ROUTES.setUserInfo);
  const { mutate: getUserInfo, isPending: isGetUserPending } = usePost(
    CPLAT_API_ROUTES.getUserInfo,
  );

  const dispatch = useAppDispatch();

  const isChangeName = newStoreName === checkedNewStoreName;
  const isChangeImg = StoreThumbnailImageUrl !== profileImgUrl;

  const handleDuplicateStoreName = () => {
    if (newStoreName.length >= 3) {
      checkDuplicateStoreName(
        { storeName: newStoreName },
        {
          onSuccess(res) {
            const { code } = res.data as CplatApiResponse<string>;

            // 중복이 아닐 경우
            if (code === '200') {
              setError('newStoreName', { type: 'ok', message: '사용 가능한 닉네임입니다' });
              setValue('checkedNewStoreName', newStoreName);

              setTimeout(() => {
                setError('newStoreName', {});
              }, 3000);
            }

            // 중복일 경우
            if (code === '614') {
              setError('newStoreName', {
                type: 'duplicate',
                message: '이미 사용중인 닉네임입니다',
              });

              setTimeout(() => {
                setError('newStoreName', {});
              }, 3000);
            }
          },
        },
      );
    }
  };
  const handleChnageProfileImg = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      if (e.target.files[0].size > 10 * 1024 * 1024) {
        showToast('warning', '이미지는 10MB 이하로 업로드해주세요.');
        return;
      }

      const url = URL.createObjectURL(e.target.files[0]);
      setValue('profileImgUrl', url);
      setValue('profileFile', e.target.files[0]);
    }
  };

  const handleSubmit = async (
    profileImgUrl: string,
    profileFile?: File,
    checkedNewStoreName?: string,
  ) => {
    let body = { ...user };

    // 함수 내부에서 변경 여부 판단
    const isChangeImg = user.StoreThumbnailImageUrl !== profileImgUrl;
    const isChangeName = user.StoreName !== checkedNewStoreName;

    // 프로필 이미지 업로드
    if (profileFile && isChangeImg) {
      setIsImgUploading(true);
      const { status, data } = await UploadSystem.uploadImgAsync(profileFile);

      if (status == 200) {
        const { data: cplatData } = data as CplatApiResponse<{ ImageUrl: string }[]>;
        body = { ...body, StoreThumbnailImageUrl: cplatData[0].ImageUrl };
        setIsImgUploading(false);
        setUserEventHistory({
          eventName: 'update_profile',
        });
      } else {
        showToast('warning', '이미지 업로드 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
        setIsImgUploading(false);
        return;
      }
    }

    // 이름 변경이 있으면 body에 반영
    if (isChangeName) {
      body = { ...body, StoreName: checkedNewStoreName };
    }

    // 변경이 있으면 서버에 요청
    if (isChangeImg || isChangeName) {
      setUserInfo(body, {
        onSuccess(res) {
          const { code } = res.data as CplatApiResponse<undefined>;
          if (code === '200') {
            getUserInfo(
              { cplatToken: user.CplatToken },
              {
                onSuccess(res) {
                  const { data } = res.data as CplatApiResponse<UserData[]>;
                  setError('newStoreName', {});
                  setValue('checkedNewStoreName', '');
                  setValue('profileFile', undefined);
                  setValue('profileImgUrl', data[0]?.StoreThumbnailImageUrl);

                  if (code === '200') {
                    if (data.length === 0) {
                      showToast(
                        'warning',
                        '인증이 만료되었습니다. 셀러박스 앱을 통해 다시 인증해주세요.',
                      );
                    } else {
                      dispatch(setUser({ ...data[0] }));
                    }
                  } else {
                    showToast(
                      'warning',
                      '인증이 만료되었습니다. 셀러박스 앱을 통해 다시 인증해주세요.',
                    );
                  }
                },
              },
            );
          } else {
            showToast('warning', '업로드 중 에러가 발생했습니다. 잠시 후 다시 시도해 주세요');
          }
        },
      });
    }

    setIsOpen(false);
  };

  const [isApplying, setIsApplying] = useState(false);

  const handleApply = (
    tempProfileImgUrl: string,
    tempProfileFile?: File,
    checkedNewStoreName?: string,
  ) => {
    setValue('profileImgUrl', tempProfileImgUrl);
    setValue('profileFile', tempProfileFile);
    handleSubmit(tempProfileImgUrl, tempProfileFile, checkedNewStoreName);

    setIsOpen(false);
  };
  return (
    <LayoutTabSection>
      <div className="flex items-center justify-between gap-[16px]">
        <div className="flex items-center gap-[24px]">
          <ProfileIconSection formValues={formValues} />
          <div className="flex flex-col">
            <div className="flex items-start gap-1">
              <UserTitle formValues={formValues} />
              <Link to={ROUTES.updateInfo}>
                <PencilIcon className="cursor-pointer" />
              </Link>
            </div>
          </div>
        </div>
        <GetOutClient />
      </div>
    </LayoutTabSection>
  );
};

export default TabSection;

const UserTitle = ({ formValues }: { formValues: FormData }) => {
  const isMember = useCheckMembership();
  const user = useAppSelector((state) => state.user);
  return (
    <div className="flex flex-col gap-[8px]">
      <h1 className="text-gr-900 Title2S24">{user.StoreName} 님</h1>
      {isMember && (
        <div className="flex items-center gap-[8px] text-gr-700 Body2M16">맴버십 이용중</div>
      )}
    </div>
  );
};

const ProfileIconSection = ({ formValues }: { formValues: FormData }) => {
  return (
    <div className="flex items-center justify-center">
      {formValues.profileImgUrl ? <HaveProfileIcon formValues={formValues} /> : <NoneProfileIcon />}
    </div>
  );
};

const HaveProfileIcon = ({ formValues }: { formValues: FormData }) => {
  return (
    <Avatar className="h-[100px] w-[100px] rounded-full border  border-gr-300 bg-white shadow-none">
      <AvatarImage className="rounded-full" src={formValues.profileImgUrl} alt="프로필 이미지" />
      <AvatarFallback>
        <ProfileIcon />
      </AvatarFallback>
    </Avatar>
  );
};

const NoneProfileIcon = () => {
  return (
    <div className="flex h-[100px] w-[100px] items-center justify-center rounded-full border border-gr-300 bg-white">
      <ProfileIcon width={64} height={64} />
    </div>
  );
};
